import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  cancelShift,
  deleteDayAnnotation,
  formatTime,
  listDayAnnotations,
  listShiftsForSchedule,
  upsertDayAnnotation,
} from '@/lib/scheduling';
import { notify } from '@/lib/notify';
import type { DayAnnotation, Shift } from '@/lib/types';
import { useLocations } from '@/stores/locations';
import { useRole } from '@/stores/identity';
import { supabase } from '@/lib/supabase';
import {
  CrossOrgReadOnlyBanner,
  useIsReadOnlyCrossOrg,
} from '@/components/CrossOrgReadOnlyBanner';

/**
 * Day-detail view for /schedule/:locationId/:yyyymm/day/:date.
 *
 * Pilot scope:
 *   - Hourly timeline showing each shift on that date as a labelled block.
 *   - Day annotation editor (one per location/date; upsert / delete).
 *   - Cancel-shift action (owner / manager only).
 *
 * Future (Day 6+):
 *   - Drag-resize / drag-create shift directly on the timeline.
 *   - Show pharmacist-coverage gaps as red bands.
 */
export default function ScheduleDay() {
  const { locationId, yyyymm, date } = useParams<{
    locationId: string;
    yyyymm: string;
    date: string;
  }>();
  const role = useRole();
  const baseCanEdit = role === 'owner' || role === 'manager';

  const locations = useLocations((s) => s.items);
  const locStatus = useLocations((s) => s.status);
  const loadLocations = useLocations((s) => s.load);
  const location = locations.find((l) => l.id === locationId) ?? null;

  // Cross-org admin: gate edits. Schedule mutation RPCs (cancelShift,
  // upsertDayAnnotation, deleteDayAnnotation) refuse to act on rows
  // belonging to a different organization, so we hide the write surfaces
  // and surface a banner.
  const isReadOnlyCrossOrg = useIsReadOnlyCrossOrg(location?.organization_id);
  const canEdit = baseCanEdit && !isReadOnlyCrossOrg;

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [annotation, setAnnotation] = useState<DayAnnotation | null>(null);
  const [scheduleStatus, setScheduleStatus] = useState<
    'draft' | 'published' | 'closed' | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!locationId || !yyyymm || !date) return;
    setLoading(true);
    setError(null);
    try {
      // Find or create the schedule for this month, then load shifts on this day
      const monthStart = `${yyyymm}-01`;
      const { data: schedRows, error: schedErr } = await supabase
        .from('schedules')
        .select('id, status')
        .eq('location_id', locationId)
        .eq('month_start_date', monthStart);
      if (schedErr) throw schedErr;
      const schedRow = schedRows?.[0];
      setScheduleStatus((schedRow?.status as 'draft' | 'published' | 'closed' | undefined) ?? null);
      if (!schedRow?.id) {
        setShifts([]);
      } else {
        const all = await listShiftsForSchedule(schedRow.id);
        setShifts(all.filter((s) => s.shift_date === date));
      }

      // Pull this date's annotation (if any)
      const annotations = await listDayAnnotations(locationId, monthStart);
      setAnnotation(annotations.find((a) => a.annotation_date === date) ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [locationId, yyyymm, date]);

  useEffect(() => {
    if (locStatus === 'idle') void loadLocations();
    void reload();
  }, [locStatus, loadLocations, reload]);

  if (!locationId || !yyyymm || !date) {
    return <div className="p-8">Invalid URL.</div>;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {isReadOnlyCrossOrg && (
          <CrossOrgReadOnlyBanner
            entityLabel="schedule"
            blockedActionsLabel="shift and day-annotation actions"
          />
        )}
        <Link
          to={`/schedule/${locationId}/${yyyymm}`}
          className="text-sm text-muted-foreground underline"
        >
          ← Back to month
        </Link>

        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {formatLocalDateLong(date)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {location?.name ?? '...'}
          </p>
        </div>

        {error && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading day...</p>
        ) : (
          <>
            <DayAnnotationEditor
              locationId={locationId}
              date={date}
              annotation={annotation}
              // 0094 follow-up: closed-schedule guard. The day_annotation_*
              // RPCs refuse to mutate inside a closed schedule (42501), so
              // hide the Add / Edit / Delete buttons when scheduleStatus
              // is 'closed'. Existing annotations are still rendered
              // read-only (the editor's no-canEdit branch).
              canEdit={canEdit && scheduleStatus !== 'closed'}
              onSaved={() => void reload()}
            />

            <Timeline
              shifts={shifts}
              canEdit={canEdit}
              scheduleClosed={scheduleStatus === 'closed'}
              onCancel={async (id) => {
                const reason = prompt('Reason for cancellation? (recorded in audit log)');
                if (!reason || !reason.trim()) return;
                try {
                  await cancelShift(id, reason.trim());
                  await reload();
                  notify.success('Shift cancelled');
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ----- annotation editor ---------------------------------------------------

function DayAnnotationEditor({
  locationId,
  date,
  annotation,
  canEdit,
  onSaved,
}: {
  locationId: string;
  date: string;
  annotation: DayAnnotation | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(annotation?.note ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(annotation?.note ?? '');
  }, [annotation]);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    if (!draft.trim()) {
      setError('Note cannot be empty.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await upsertDayAnnotation(locationId, date, draft.trim());
      setEditing(false);
      onSaved();
      notify.success('Day note saved');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete() {
    if (!confirm('Delete this day note?')) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteDayAnnotation(locationId, date);
      setEditing(false);
      onSaved();
      notify.info('Day note removed');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!annotation && !editing) {
    return canEdit ? (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground hover:bg-muted/30 w-full text-left"
      >
        + Add a day note (e.g. "renovation in progress", "training at 2pm")
      </button>
    ) : null;
  }

  if (!editing) {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-4 space-y-2">
        <p className="text-sm">{annotation!.note}</p>
        {canEdit && (
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="text-destructive hover:underline"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSave} className="rounded-md border border-border bg-card p-4 space-y-2">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Day note (visible to staff working on this date)"
        rows={3}
        className={inputClass}
      />
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(annotation?.note ?? '');
            setError(null);
          }}
          className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted/40"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save note'}
        </button>
      </div>
    </form>
  );
}

// ----- hourly timeline -----------------------------------------------------

function Timeline({
  shifts,
  canEdit,
  scheduleClosed,
  onCancel,
}: {
  shifts: Shift[];
  canEdit: boolean;
  scheduleClosed: boolean;
  onCancel: (id: string) => Promise<void>;
}) {
  const layout = useMemo(() => computeLayout(shifts), [shifts]);

  if (shifts.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No shifts on this day.
      </div>
    );
  }

  // Render hour markers from earliest 6am to latest 11pm (covers most pharmacy days)
  // The block height per hour is 48px.
  const HOUR_PX = 48;
  const startHour = 6;
  const endHour = 23;
  const totalHours = endHour - startHour;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="relative" style={{ height: totalHours * HOUR_PX }}>
        {/* hour grid */}
        {Array.from({ length: totalHours + 1 }).map((_, i) => {
          const hour = startHour + i;
          return (
            <div
              key={hour}
              className="absolute left-0 right-0 border-t border-border/60 text-xs text-muted-foreground"
              style={{ top: i * HOUR_PX }}
            >
              <span className="absolute -top-2 left-0 bg-card pr-1">
                {String(hour).padStart(2, '0')}:00
              </span>
            </div>
          );
        })}

        {/* shift blocks */}
        {layout.map((block) => {
          const sh = block.shift;
          const startMin = timeToMinutes(sh.start_time);
          const endMin = timeToMinutes(sh.end_time);
          const top = ((startMin - startHour * 60) / 60) * HOUR_PX;
          const height = Math.max(((endMin - startMin) / 60) * HOUR_PX, 24);
          const widthPct = 100 / block.lanes;
          const leftPct = block.lane * widthPct;

          return (
            <div
              key={sh.id}
              className={`absolute rounded-md border p-1 text-xs overflow-hidden ${
                sh.cancelled_at
                  ? 'bg-muted/40 border-muted text-muted-foreground line-through'
                  : sh.is_open
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-emerald-50 border-emerald-300 text-emerald-900'
              }`}
              style={{
                top,
                height,
                left: `calc(60px + ${leftPct}% * (100% - 60px) / 100%)`,
                width: `calc(${widthPct}% * (100% - 60px) / 100% - 4px)`,
              }}
              title={sh.notes ?? sh.staff_display_name_at_save ?? 'Open shift'}
            >
              <div className="font-medium truncate">
                {sh.is_open ? 'Open' : sh.staff_display_name_at_save ?? '—'}
              </div>
              <div className="text-[10px]">
                {formatTime(sh.start_time)}–{formatTime(sh.end_time)}
              </div>
              {sh.override_reason && (
                <div className="text-[10px] text-amber-700">⚠ override</div>
              )}
              {canEdit && !sh.cancelled_at && !scheduleClosed && (
                <button
                  type="button"
                  onClick={() => void onCancel(sh.id)}
                  className="absolute top-0.5 right-0.5 text-[10px] text-destructive hover:underline"
                  title="Cancel shift"
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Lane-pack overlapping shifts (greedy left-to-right).
function computeLayout(
  shifts: Shift[]
): Array<{ shift: Shift; lane: number; lanes: number }> {
  const sorted = [...shifts].sort((a, b) =>
    a.start_time.localeCompare(b.start_time)
  );
  const lanes: Shift[][] = [];
  const placement = new Map<string, number>();
  for (const sh of sorted) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const last = lanes[i][lanes[i].length - 1];
      if (last.end_time <= sh.start_time) {
        lanes[i].push(sh);
        placement.set(sh.id, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([sh]);
      placement.set(sh.id, lanes.length - 1);
    }
  }
  const totalLanes = Math.max(lanes.length, 1);
  return sorted.map((sh) => ({
    shift: sh,
    lane: placement.get(sh.id) ?? 0,
    lanes: totalLanes,
  }));
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function formatLocalDateLong(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
